import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import {
  saveArenaUsername,
  validateDisplayName,
  hasRealProfile,
} from '../api/userProfileService';
import { peekPendingTicketCode, buildPendingTicketResumePath } from '../utils/challengeTicketIntent';
import AuthLayout from '../layouts/AuthLayout';
import StadiumShell from '../layouts/StadiumShell';
import { Alert, Button, InputField, TextInput } from '../components/ui';
import { typography } from '../design-system/tokens';

/**
 * After email/Google sign-in: arena name alone completes onboarding (Option A).
 * Sport, avatar, and bio are profile enhancements inside The Lockbox.
 */
function CreateProfilePage({
  authReady = true,
  firebaseUser,
  profileDoc,
  onProfileComplete,
  onProfileAlreadyExists,
}) {
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const skipOnce = useRef(false);

  useEffect(() => {
    if (!firebaseUser?.displayName) return;
    setUsername((prev) => (prev.trim() ? prev : firebaseUser.displayName.replace(/\s+/g, '')));
  }, [firebaseUser]);

  const handleAlreadyHasProfile = useCallback(() => {
    if (skipOnce.current) return;
    skipOnce.current = true;
    onProfileAlreadyExists?.();
    const pendingTicket = peekPendingTicketCode();
    if (pendingTicket) {
      navigate(buildPendingTicketResumePath(pendingTicket), { replace: true });
      return;
    }
    navigate('/app', { replace: true });
  }, [navigate, onProfileAlreadyExists]);

  useEffect(() => {
    if (hasRealProfile(profileDoc)) {
      handleAlreadyHasProfile();
    }
  }, [profileDoc, handleAlreadyHasProfile]);

  if (!authReady) {
    return (
      <StadiumShell className="flex items-center justify-center">
        <p className={`${typography.body} font-bold`}>Loading…</p>
      </StadiumShell>
    );
  }

  if (!firebaseUser) {
    return <Navigate to="/signup?mode=login" replace />;
  }

  if (profileDoc === undefined) {
    return (
      <StadiumShell className="flex items-center justify-center">
        <p className={`${typography.body} font-bold`}>Loading…</p>
      </StadiumShell>
    );
  }

  const handleArenaNameSubmit = async (e) => {
    e.preventDefault();
    setError('');
    const nameErr = validateDisplayName(username);
    if (nameErr) {
      setError(nameErr);
      return;
    }
    setSaving(true);
    try {
      const saved = await saveArenaUsername(firebaseUser.uid, username);
      onProfileComplete?.(saved);
      const pendingTicket = peekPendingTicketCode();
      if (pendingTicket) {
        navigate(buildPendingTicketResumePath(pendingTicket), { replace: true });
      } else {
        navigate('/app', { replace: true });
      }
    } catch (err) {
      setError(err?.message || 'Could not save arena name. Try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <AuthLayout showBack={false}>
      <h1 className={typography.heading}>Create Your Arena Name</h1>
      <p className={`${typography.body} mb-6`}>
        This is how other players find you in THE LOCKBOX. Choose a unique name — 3–20 characters, no spaces.
        You can add a photo, sport, and bio later from your profile.
      </p>

      {error && <Alert tone="error" className="mb-4">{error}</Alert>}

      <form onSubmit={handleArenaNameSubmit} className="space-y-4">
        <InputField label="Arena name">
          <TextInput
            type="text"
            value={username}
            onChange={(e) => setUsername(e.target.value.replace(/\s/g, ''))}
            autoComplete="nickname"
            placeholder="e.g. SportsKing"
            maxLength={20}
          />
        </InputField>

        <Button type="submit" fullWidth disabled={saving} className="mt-6">
          {saving ? 'Saving…' : 'Enter Lockbox'}
        </Button>
      </form>
    </AuthLayout>
  );
}

export default CreateProfilePage;
